// The transport behind the generated backend hooks.
//
// Retool generated one hook per backend function with this shape, and the pages
// depend on every part of it:
//
//   const q = useGetAnalytics()
//   q.data      // unknown — pages cast it themselves
//   q.loading   // true while a trigger is in flight
//   q.error     // string | null, shown in toasts and inline
//   q.trigger(params?, { skipCache? })  // resolves the result, THROWS on failure
//
// `trigger` both rejecting and setting `.error` is deliberate: some call sites
// wrap it in try/catch (ShippingPage), others read `.error` from the hook
// (DashboardPage). Both must keep working.
//
// `data` resets to null on mount, which is why hooks/cachedQueries.ts exists —
// do not "fix" that here without reading that file first.

import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * What `trigger()` hands back.
 *
 * Retool returned a value that was both awaitable and carried a `.result`
 * promise, and the pages use both forms:
 *
 *   const rows = await getProducts.trigger()          // InventoryPage
 *   const res  = await runReview.trigger({}).result   // TierReviewPanel
 *   runReview.trigger({}).result.then(...)            // TierReviewAutoRun
 *
 * So this is a real Promise with `result` pointing at itself.
 */
export type TriggerResult<T> = Promise<T> & { result: Promise<T> }

export interface BackendQuery<T = unknown> {
  data: T | null
  loading: boolean
  error: string | null
  trigger: (params?: Record<string, unknown>, options?: TriggerOptions) => TriggerResult<T>
}

export interface TriggerOptions {
  /**
   * Bypass in-flight deduplication. Retool used this to force a refetch; here
   * every call already hits the server, so this only matters when two identical
   * requests overlap.
   */
  skipCache?: boolean
}

/** Overridable so the frontend can be served from a different origin in dev. */
const API_BASE = (import.meta.env?.VITE_API_BASE ?? '').replace(/\/+$/, '')

/** Identical concurrent requests share one round-trip. */
const inFlight = new Map<string, Promise<unknown>>()

export class BackendError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message)
    this.name = 'BackendError'
  }
}

/** Where an unauthenticated response sends the browser. */
function onUnauthenticated(): void {
  // A dead session should land on the login screen rather than fail silently
  // behind every panel on the page.
  if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
    const next = encodeURIComponent(window.location.pathname + window.location.search)
    window.location.assign(`/login?next=${next}`)
  }
}

export async function callBackend<T>(
  group: string,
  name: string,
  params: Record<string, unknown> = {},
  options: TriggerOptions = {},
): Promise<T> {
  const key = `${group}/${name}:${JSON.stringify(params)}`
  if (!options.skipCache) {
    const existing = inFlight.get(key)
    if (existing) return existing as Promise<T>
  }

  const pending = (async (): Promise<T> => {
    const response = await fetch(`${API_BASE}/api/rpc/${group}/${name}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // Session cookie.
      credentials: 'same-origin',
      body: JSON.stringify(params),
    })

    if (response.status === 401) {
      onUnauthenticated()
      throw new BackendError('Your session ended. Sign in again.', 401)
    }

    const text = await response.text()
    let payload: unknown = null
    if (text !== '') {
      try {
        payload = JSON.parse(text)
      } catch {
        payload = text
      }
    }

    if (!response.ok) {
      // The server passes the endpoint's own error message through; these are
      // written for staff to read, so prefer them over a generic string.
      const message =
        (payload as { error?: string } | null)?.error ??
        (typeof payload === 'string' && payload ? payload : `Request failed (${response.status})`)
      throw new BackendError(message, response.status)
    }

    return payload as T
  })()

  inFlight.set(key, pending)
  try {
    return await pending
  } finally {
    inFlight.delete(key)
  }
}

/** Builds the hook that the generated modules export. */
export function makeBackendHook<T = unknown>(group: string, name: string) {
  return function useBackendQuery(): BackendQuery<T> {
    const [data, setData] = useState<T | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    // Avoid setting state after unmount, and stop a slow early response from
    // overwriting a faster later one.
    const mounted = useRef(true)
    const sequence = useRef(0)
    useEffect(() => {
      mounted.current = true
      return () => {
        mounted.current = false
      }
    }, [])

    const trigger = useCallback(
      (params?: Record<string, unknown>, options?: TriggerOptions): TriggerResult<T> => {
        const ticket = ++sequence.current
        if (mounted.current) {
          setLoading(true)
          setError(null)
        }

        const run = async (): Promise<T> => {
          try {
            const result = await callBackend<T>(group, name, params ?? {}, options ?? {})
            if (mounted.current && ticket === sequence.current) {
              setData(result)
              setLoading(false)
            }
            return result
          } catch (thrown) {
            const message = thrown instanceof Error ? thrown.message : String(thrown)
            if (mounted.current && ticket === sequence.current) {
              setError(message)
              setLoading(false)
            }
            // Call sites also try/catch around trigger, so keep rejecting.
            throw thrown
          }
        }

        const pending = run() as TriggerResult<T>
        // `.result` is the same promise, so awaiting either settles once.
        pending.result = pending
        return pending
      },
      [],
    )

    return { data, loading, error, trigger }
  }
}
